import React, { useState } from "react";
import { createApiClient } from '../lib/apiClient';
import { useUserId } from "../UserIdContext";
import "./usuarios.css";

const api = createApiClient({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:3220",
  headers: { "Content-Type": "application/json" },
});

const normalizeEstado = (estado) =>
  String(estado || "disponible").toLowerCase();

const fmtDate = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  } catch {
    return "";
  }
};

export default function AnularRemito() {
  const { deposito } = useUserId();
  const depositoActual = deposito || "binder1";

  const [remito, setRemito] = useState("");
  const [motivo, setMotivo] = useState("");
  const [ingresos, setIngresos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [anulandoId, setAnulandoId] = useState(null);
  const [mensaje, setMensaje] = useState(null);

  const setFeedback = (text, tone = "info") => {
    setMensaje({ text, tone, ts: Date.now() });
    setTimeout(() => setMensaje(null), 4500);
  };

  const buscarRemito = async () => {
    const nro = remito.trim();
    if (!nro) {
      setFeedback("Ingrese un nro de remito", "warn");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/api/anular", {
        params: { remito: nro, deposito: depositoActual },
      });
      const list = Array.isArray(data?.data?.ingresos)
        ? data.data.ingresos
        : [];
      setIngresos(list);
      if (!list.length) {
        setFeedback("Sin resultados", "warn");
      }
    } catch (err) {
      setFeedback(
        err?.response?.data?.error || "No pude buscar el remito",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const anularIngreso = async (ingreso) => {
    if (!ingreso?._id || anulandoId) return;
    const nro = ingreso.nroRemito || ingreso.remito || "";
    const estado = normalizeEstado(ingreso.estado);
    if (estado === "anulado") return;
    const ok = window.confirm(
      `Anular remito ${nro}? Esta accion no se puede deshacer.`
    );
    if (!ok) return;

    setAnulandoId(ingreso._id);
    try {
      const payload = { ingresoId: ingreso._id, deposito: depositoActual };
      if (motivo.trim()) payload.motivo = motivo.trim();
      const { data } = await api.post("/api/anular", payload);
      setIngresos((prev) =>
        prev.map((it) =>
          it._id === ingreso._id
            ? {
                ...it,
                estado: "anulado",
                fechaAnulacion:
                  data?.fechaAnulacion || new Date().toISOString(),
                motivoAnulacion: motivo.trim() || it.motivoAnulacion || "",
              }
            : it
        )
      );
      setFeedback("Remito anulado", "ok");
    } catch (err) {
      setFeedback(
        err?.response?.data?.error || "No pude anular el remito",
        "error"
      );
    } finally {
      setAnulandoId(null);
    }
  };

  return (
    <div className="users-wrap">
      <div className="users-header">
        <div className="title-stack">
          <h1>Anular remito</h1>
          <p className="subtitle">
            Busca por nro de remito y confirma la anulacion
          </p>
        </div>
   
      </div>

      <div className="panel" style={{ marginBottom: 12 }}>
        <div className="form-grid drawer-grid">
          <div className="field">
            <label>Nro de remito</label>
            <input
              value={remito}
              onChange={(e) => setRemito(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") buscarRemito();
              }}
              placeholder="Ej: R-000123"
              style={{ textAlign: "center" }}
            />
          </div>
          <div className="field">
            <label>Motivo (opcional)</label>
            <input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej: remito duplicado"
              style={{ textAlign: "center" }}
            />
          </div>
          <div className="field" style={{ alignSelf: "flex-end" }}>
            <button
              type="button"
              className="btn solid"
              onClick={buscarRemito}
              disabled={loading}
            >
              {loading ? "Buscando..." : "Buscar remito"}
            </button>
          </div>
        </div>
      </div>

      {mensaje && (
        <div
          className="panel"
          style={{
            marginBottom: 12,
            borderColor:
              mensaje.tone === "error"
                ? "#5a2d2d"
                : mensaje.tone === "warn"
                ? "#735d24"
                : "#1a4f42",
          }}
        >
          {mensaje.text}
        </div>
      )}

      {ingresos.map((ingreso) => {
        const estado = normalizeEstado(ingreso.estado);
        const cliente =
          ingreso?.cliente?.nombreCliente ||
          ingreso?.cliente?.codCliente ||
          ingreso?.cliente?.CUIT ||
          "Cliente sin nombre";
        const nro = ingreso.nroRemito || ingreso.remito || "-";
        const pallets = Array.isArray(ingreso.pallets)
          ? ingreso.pallets
          : [];

        return (
          <div className="panel" key={ingreso._id} style={{ marginBottom: 12 }}>
            <div className="section-header">
              <div className="title-stack">
                <h2 style={{ margin: 0 }}>Remito {nro}</h2>
                <p className="subtitle">
                  Cliente: {cliente} | Fecha: {fmtDate(ingreso.fechaRemito)}
                </p>
              </div>
              <div className="toolbar">
                <span className={`badge ${estado !== "anulado" ? "ok" : ""}`}>
                  {estado}
                </span>
                <button
                  type="button"
                  className="btn danger"
                  onClick={() => anularIngreso(ingreso)}
                  disabled={estado === "anulado" || anulandoId === ingreso._id}
                >
                  {anulandoId === ingreso._id ? "Anulando..." : "Anular"}
                </button>
              </div>
            </div>

            {estado === "anulado" && ingreso.fechaAnulacion && (
              <div className="subtitle" style={{ marginBottom: 8 }}>
                Anulado el {fmtDate(ingreso.fechaAnulacion)}
              </div>
            )}

            <div className="table-responsive">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th>Nro pallet</th>
                    <th>Identificacion</th>
                    <th>Producto</th>
                    <th>Lote</th>
                    <th className="mono">Cantidad</th>
                    <th>Condicion</th>
                    <th>F. elab</th>
                    <th>F. vto</th>
                  </tr>
                </thead>
                <tbody>
                  {pallets.length ? (
                    pallets.map((p) => (
                      <tr key={p._id}>
                        <td>{p.nPallet || "-"}</td>
                        <td>{p.identificacion || "-"}</td>
                        <td>
                          {p.detalleProductoTexto ||
                            p.fcGrupoProveedor ||
                            p.codigo ||
                            "-"}
                        </td>
                        <td>{p.loteTexto || "-"}</td>
                        <td className="mono">
                          {Number.isFinite(+p.cantidad)
                            ? p.cantidad
                            : p.unidadesPorPallet || 0}
                        </td>
                        <td>{p.condicion || "-"}</td>
                        <td>{fmtDate(p.fechaElaboracion)}</td>
                        <td>{fmtDate(p.fechaVencimiento)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8}>Sin pallets asociados</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
